import { useEffect, useState, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ripple from "assets/img/ripple.gif";
import { Spinner } from "react-bootstrap";
import { faker } from "@faker-js/faker";
import styled from "@emotion/styled";
import { DefaultModalProps, ModalType } from "types/Modal";

type ButtonConfig = {
    variant: string;
    text: string;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
};

export const CustomModal = styled(Modal)`
    & .modal-dialog {
        max-width: ${(props) => (props.width || 500) + 2 + "px"};
    }

    & .modal-date-picker .MuiFormControl-root {
        width: 100%;
        background: #f0f0f0 !important;
        min-width: 100% !important;
        border-radius: 4px !important;

        & .MuiOutlinedInput-root {
            height: 36px;

            & input,
            & svg {
                color: #4c4c4c !important;
            }
        }

        & fieldset {
            border: none !important;
        }
    }

    &.updateConfirm .modal-body {
        .btn {
            &.btn-danger,
            &.btn-primary {
                text-transform: uppercase;
            }
        }
    }
`;

const DefaultModal = ({
    modalShow,
    setModalShow,
    modalType = "",
    modalHeaderButton,
    modalContent,
    modalButton,
    deleteFunction,
    resendFunction,
    okAction,
    errorArray,
    modalTitle,
    isLoading,
    modalIcon,
    iconVariant = "primary",
    modalFooter,
    modalData,
    width,
}: DefaultModalProps) => {
    /*
    Props
    modalShow: boolean,
    setModalShow: function(boolean),
    modalType: string
        "ok" - Green tick icon, "OK" button to close modal
        "err" - Red Cross icon, "OK" button to close modal
        "dlt" - Trash icon,"Cancel" button to close modal and "Delete" button to execute deleteFunction
        "dltok" - Green tick icon, "OK" button to trigger okAction
    modalContent: string,
    deleteFunction: function(),
    okAction: any
        Eg, history.push() or setModalShow(false);
    errorArray: array,
    modalTitle: string,
    isLoading: boolean,
    modalIcon: string
    */
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (modalShow) {
            setIsProcessing(false);
        }
    }, [modalShow]);

    const renderModalIcon = () => {
        if (modalIcon) {
            return (
                <span
                    className={
                        iconVariant === "primary"
                            ? "material-icons bg-primary-green-1"
                            : "material-icons bg-secondary-red-1"
                    }
                >
                    {modalIcon}
                </span>
            );
        }
        const iconMap: Record<string, string> = {
            err: "close",
            dlt: "delete",
            resendConfirm: "forward_to_inbox",
            resendConfirmOk: "done",
            updateConfirm: "help",
        };
        const icon = iconMap[modalType] || "done";
        const className = ["err", "dlt"].includes(modalType)
            ? "material-icons"
            : "material-icons bg-primary-green-1";
        return <span className={className}>{icon}</span>;
    };
    const renderModalTitle = () => {
        if (modalTitle) {
            return <h3 className="mb-3">{modalTitle}</h3>;
        }
        const titleMap: Record<string, string> = {
            err: "Error",
            dlt: "Confirm Delete",
            resendConfirmOk: "New Code Sent",
        };
        const title =
            titleMap[modalType] ||
            (["resendConfirm", "updateConfirm"].includes(modalType)
                ? "Confirmation"
                : "Success");
        return <h3 className="mb-3">{title}</h3>;
    };
    const getModalButtons = () => {
        const buttonConfigs: Record<string, ButtonConfig[]> = {
            dlt: [
                {
                    variant: "secondary",
                    text: "CANCEL",
                    onClick: (event) => setModalShow?.(false),
                },
                {
                    variant: "danger",
                    text: "DELETE",
                    onClick: (event) => {
                        event.preventDefault();
                        setIsProcessing(true);
                        deleteFunction?.();
                    },
                    disabled: isProcessing,
                },
            ],
            dltok: [
                {
                    variant: "primary",
                    text: "OK",
                    onClick: (event) => okAction?.(),
                },
            ],
            resendConfirmOk: [
                {
                    variant: "primary",
                    text: "OK",
                    onClick: (event) => okAction?.(),
                },
            ],
            cancelConfirmOk: [
                {
                    variant: "primary",
                    text: "OK",
                    onClick: (event) => okAction?.(),
                },
            ],
            scanok: [
                {
                    variant: "primary",
                    text: "OK",
                    onClick: (event) => okAction?.(),
                },
            ],
            resendConfirm: [
                {
                    variant: "secondary",
                    text: "NO",
                    onClick: (event) => setModalShow?.(false),
                },
                {
                    variant: "primary",
                    text: "YES",
                    onClick: (event) => {
                        setModalShow?.(false);
                        resendFunction?.();
                    },
                },
            ],
            cancelConfirm: [
                {
                    variant: "secondary",
                    text: "NO",
                    onClick: (event) => setModalShow?.(false),
                },
                {
                    variant: "primary",
                    text: "YES",
                    onClick: (event) => {
                        setModalShow?.(false);
                        resendFunction?.();
                    },
                },
            ],
            updateConfirm: [
                {
                    variant: "danger",
                    text: "NO",
                    onClick: (event) => setModalShow?.(false),
                },
                {
                    variant: "primary",
                    text: "YES",
                    onClick: (event) => {
                        setModalShow?.(false);
                        resendFunction?.();
                    },
                },
            ],
            disableConfirm: [
                {
                    variant: "secondary",
                    text: "CANCEL",
                    onClick: (event) => setModalShow?.(false),
                },
                {
                    variant: "danger",
                    text: "DISABLE",
                    onClick: (event) => {
                        setModalShow?.(false);
                        resendFunction?.();
                    },
                },
            ],
        };
        const config = buttonConfigs[modalType];
        if (config) {
            return config.map((btn: ButtonConfig, index: number) => (
                <Button
                    key={`${btn.text}_${index}`}
                    variant={btn.variant}
                    disabled={btn.disabled}
                    onClick={btn.onClick}
                >
                    {btn.text}
                </Button>
            ));
        }
        return (
            <Button
                variant="primary"
                onClick={() => {
                    okAction?.();
                    setModalShow?.(false);
                }}
            >
                OK
            </Button>
        );
    };

    const renderErrorArray = () => {
        if (errorArray) {
            return errorArray.map((error: any) => {
                return (
                    <p
                        role="alert"
                        className="mb-4"
                        key={faker.datatype.uuid()}
                    >
                        {error}
                    </p>
                );
            });
        }
    };

    const getModalClassName = () => {
        let className = modalType;
        if (modalType !== "content") className += " no-header";
        if (
            [
                "ok",
                "resendConfirm",
                "cancelConfirm",
                "resendConfirmOk",
                "updateConfirm",
            ].includes(modalType)
        ) {
            className += " primary";
        } else {
            className += " danger";
        }
        return className;
    };

    const renderBodyContent = () => {
        if (modalType === "content") {
            return typeof modalContent === "function"
                ? modalContent(modalData)
                : modalContent;
        }

        const getContent = () => {
            if (modalType === "custom") {
                return typeof modalContent === "function"
                    ? modalContent(modalData)
                    : modalContent;
            }
            return modalContent;
        };

        const getButton = () => {
            if (modalType === "custom") {
                return typeof modalButton === "function"
                    ? modalButton(modalData)
                    : modalButton;
            }
            return getModalButtons();
        };

        const shouldShowIcon =
            (modalType !== "custom" && modalType !== "disableConfirm") ||
            modalIcon;

        return (
            <>
                {shouldShowIcon && (
                    <div className="modal-icon-box">{renderModalIcon()}</div>
                )}
                {renderModalTitle()}
                <div role="alert" className="mb-4">
                    {getContent()}
                </div>
                {renderErrorArray()}
                {getButton()}
            </>
        );
    };

    return (
        <CustomModal
            centered
            show={modalShow}
            onHide={() => setModalShow?.(false)}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-sm"
            className={getModalClassName()}
            width={width}
        >
            {isLoading ? (
                <img className="loader-gif" src={ripple} alt="loading" />
            ) : (
                <>
                    {modalType === "content" && (
                        <Modal.Header>
                            <Modal.Title>{modalTitle}</Modal.Title>
                            {typeof modalHeaderButton === "function" ? (
                                modalHeaderButton(modalData)
                            ) : (
                                <Button
                                    variant=""
                                    className="close-button"
                                    onClick={() => setModalShow?.(false)}
                                >
                                    <span className="material-icons">
                                        close
                                    </span>
                                </Button>
                            )}
                        </Modal.Header>
                    )}
                    <Modal.Body
                        className={`${
                            modalType === "content"
                                ? ""
                                : "text-center mt-3 mb-3"
                        }`}
                    >
                        {renderBodyContent()}
                    </Modal.Body>
                    {modalType === "content" && !!modalFooter && (
                        <Modal.Footer>
                            {typeof modalFooter === "function"
                                ? modalFooter(modalData)
                                : modalFooter}
                        </Modal.Footer>
                    )}
                </>
            )}
        </CustomModal>
    );
};

const LoadingModal = ({ showModal = false, title = "" }) => {
    return (
        <Modal
            centered
            show={showModal}
            backdrop="static"
            keyboard={false}
            aria-labelledby="example-modal-sizes-title-sm"
            className="no-header"
        >
            {title && (
                <Modal.Title className="loading-modal-title">
                    {title}
                </Modal.Title>
            )}
            <Modal.Body className="text-center mt-3 mb-5 mr-4">
                <Spinner
                    className="centered-spinner"
                    animation="border"
                    variant="primary"
                />
            </Modal.Body>
        </Modal>
    );
};

export const useModal = (initProps?: Partial<DefaultModalProps>) => {
    const DEFAULT_MODAL_WIDTH = 500;
    const [modalShow, setModalShow] = useState<boolean>(false);
    const [modalProps, setModalProps] = useState<DefaultModalProps>({
        ...initProps,
        modalShow,
        setModalShow,
    });
    const [modalData, setModalData] = useState<Record<string, any>>({});

    useEffect(() => {
        setModalProps((prevProps) => ({ ...prevProps, modalShow }));
    }, [modalShow]);

    const openModal = useCallback(
        (
            newProps: Partial<DefaultModalProps> & {
                key: string | number;
                modalType: ModalType;
            }
        ) => {
            setModalShow(true);
            setModalProps((prevProps) => ({
                ...prevProps,
                ...newProps,
                modalShow: true,
                width: newProps.width ?? DEFAULT_MODAL_WIDTH,
            }));
        },
        []
    );
    return {
        modal: (
            <DefaultModal
                key={modalProps.key}
                {...modalProps}
                modalData={modalData}
            />
        ),
        openModal,
        setModalShow,
        modalData,
        setModalData,
    };
};

export default DefaultModal;
export { LoadingModal };
